import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Check, Loader2, Lock, ShieldCheck, CreditCard, ArrowRight, LogOut } from 'lucide-react';

const MANAGE_ROLES = ['developer', 'company_admin', 'owner_operator'];

const PLANS = [
  { key: 'basic', name: 'Basic', price: 0, tagline: 'For getting started', limits: ['Up to 10 drivers', 'Up to 10 trucks', 'Document scanning & expiry alerts', 'Audit-ready mode'], highlight: false },
  { key: 'corporate', name: 'Corporate', price: 100, tagline: 'For growing fleets', limits: ['Unlimited drivers', 'Unlimited trucks', 'Everything in Basic', 'Priority compliance automation'], highlight: true },
];

const Shell = ({ children, onSignOut }) => (
  <div className="relative min-h-[100dvh] overflow-y-auto bg-[hsl(var(--foreground))] px-4 py-10 text-white">
    <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(1000px_circle_at_15%_-10%,rgba(245,179,1,0.16),transparent_55%),radial-gradient(900px_circle_at_95%_10%,rgba(37,99,235,0.24),transparent_55%)]" />
    <div className="relative z-10 mx-auto w-full max-w-4xl">
      <div className="mb-8 flex items-center justify-between">
        <div className="[&_*]:!text-white"><Logo /></div>
        <Button variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={onSignOut} data-testid="plans-logout-button"><LogOut className="mr-1.5 h-4 w-4" />Sign out</Button>
      </div>
      {children}
    </div>
  </div>
);

export default function Plans() {
  const navigate = useNavigate();
  const { user, tenant, refreshTenant, logout } = useAuth();
  const [checkout, setCheckout] = useState(null); // plan object in checkout
  const [processing, setProcessing] = useState(false);
  const [card, setCard] = useState({ number: '4242 4242 4242 4242', exp: '12/28', cvc: '123', name: user?.name || '' });

  const canManage = MANAGE_ROLES.includes(user?.role);
  const activeTier = tenant?.subscription?.active ? tenant?.subscription?.tier : null;
  const signOut = async () => { await logout(); navigate('/login'); };

  const confirmCheckout = async (deferBilling = false) => {
    setProcessing(true);
    try {
      await client.post('/tenant/subscribe', { tier: checkout.key, defer_billing: deferBilling });
      await refreshTenant();
      toast.success(deferBilling
        ? `${checkout.name} workspace activated — you can set up billing anytime from Manage plan.`
        : `${checkout.name} plan activated — welcome aboard!`);
      setCheckout(null);
      navigate('/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate plan');
    } finally {
      setProcessing(false);
    }
  };

  // Non-admin landing on an inactive workspace
  if (!canManage && !activeTier) {
    return (
      <Shell onSignOut={signOut}>
        <Card className="mx-auto max-w-md p-8 text-center text-[hsl(var(--foreground))]">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--gold))]/15"><Lock className="h-7 w-7 text-[hsl(var(--gold))]" /></div>
          <h1 className="font-display text-xl font-semibold">Workspace not active yet</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your company admin needs to choose a subscription plan before <b>{tenant?.name || 'your workspace'}</b> unlocks. You'll get access as soon as they do.</p>
          <Button variant="outline" className="mt-6" onClick={async () => { await logout(); navigate('/login'); }} data-testid="plans-nonadmin-logout">Sign out</Button>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell onSignOut={signOut}>
      <div className="mx-auto max-w-2xl text-center">
        {activeTier ? (
          <Badge className="mb-4 border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/15" data-testid="plans-current-badge">
            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Current plan: {tenant?.subscription?.plan_name}
          </Badge>
        ) : (
          <Badge className="mb-4 border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/15" data-testid="plans-gate-badge">
            <Lock className="mr-1 h-3.5 w-3.5" /> Choose a plan to activate your workspace
          </Badge>
        )}
        <h1 className="font-display text-3xl font-bold md:text-4xl">{activeTier ? 'Manage your plan' : `Welcome, ${tenant?.name || 'let\u2019s get started'}`}</h1>
        <p className="mt-3 text-white/70">{activeTier ? 'Switch tiers at any time — changes apply instantly in this demo.' : 'Pick a subscription to unlock drivers, fleet and compliance tools. This is a demo checkout — no real payment is taken.'}</p>
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {PLANS.map((p) => {
          const isCurrent = activeTier === p.key;
          return (
            <Card key={p.key} className={`relative p-7 text-[hsl(var(--foreground))] ${p.highlight ? 'ring-1 ring-[hsl(var(--gold))]/50' : ''}`} data-testid={`plans-card-${p.key}`}>
              {p.highlight && <Badge className="absolute -top-3 right-6 bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]">Most popular</Badge>}
              <div className="text-sm font-medium text-muted-foreground">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-4xl font-bold">${p.price}</span>
                <span className="text-muted-foreground">/mo</span>
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{p.tagline}</div>
              <ul className="mt-5 space-y-2.5">
                {p.limits.map((l) => (
                  <li key={l} className="flex items-start gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--success))]" />{l}</li>
                ))}
              </ul>
              <Button
                className={`mt-6 w-full ${p.highlight && !isCurrent ? 'bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90' : ''}`}
                variant={isCurrent ? 'outline' : p.highlight ? 'default' : 'outline'}
                disabled={isCurrent}
                onClick={() => setCheckout(p)}
                data-testid={`plans-select-${p.key}`}
              >
                {isCurrent ? 'Current plan' : activeTier ? `Switch to ${p.name}` : `Choose ${p.name}`}
              </Button>
            </Card>
          );
        })}
      </div>

      {activeTier && (
        <div className="mt-8 text-center">
          <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="plans-back-dashboard">Back to dashboard <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        </div>
      )}

      {/* Simulated checkout */}
      <Dialog open={!!checkout} onOpenChange={(o) => !o && !processing && setCheckout(null)}>
        <DialogContent data-testid="plans-checkout-dialog">
          {checkout?.price === 0 ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[hsl(var(--success))]" /> Activate {checkout?.name}</DialogTitle>
                <DialogDescription>Your plan is free — no payment required. Activate your workspace in one click.</DialogDescription>
              </DialogHeader>
              <div className="rounded-lg border bg-muted/40 p-4 text-center">
                <div className="font-display text-3xl font-bold">$0<span className="text-sm font-normal text-muted-foreground">/mo</span></div>
                <div className="mt-1 text-sm text-muted-foreground">Up to 10 drivers &amp; 10 trucks included</div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCheckout(null)} disabled={processing}>Cancel</Button>
                <Button onClick={() => confirmCheckout(false)} disabled={processing} className="bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="checkout-pay-button">
                  {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{processing ? 'Activating…' : 'Activate free plan'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-[hsl(var(--primary))]" /> Checkout · {checkout?.name}</DialogTitle>
                <DialogDescription>Demo checkout — no real card is charged. Click pay to activate instantly.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border bg-muted/40 p-3 text-sm">
                  <span>{checkout?.name} plan</span>
                  <span className="font-display text-lg font-semibold">${checkout?.price}.00<span className="text-xs font-normal text-muted-foreground">/mo</span></span>
                </div>
                <div><Label className="text-xs">Name on card</Label><Input value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className="mt-1" data-testid="checkout-name" /></div>
                <div><Label className="text-xs">Card number</Label><Input value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="mt-1 font-mono" data-testid="checkout-number" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Expiry</Label><Input value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="mt-1 font-mono" data-testid="checkout-exp" /></div>
                  <div><Label className="text-xs">CVC</Label><Input value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="mt-1 font-mono" data-testid="checkout-cvc" /></div>
                </div>
                <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" /> Simulated payment for demonstration only.</p>
              </div>
              <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch sm:space-x-0">
                <Button onClick={() => confirmCheckout(false)} disabled={processing} className="w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="checkout-pay-button">
                  {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{processing ? 'Activating…' : `Pay $${checkout?.price} & activate`}
                </Button>
                <button type="button" onClick={() => confirmCheckout(true)} disabled={processing} className="mx-auto text-sm font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline disabled:opacity-50" data-testid="checkout-skip-billing">
                  Skip and set up billing later
                </button>
                <button type="button" onClick={() => setCheckout(null)} disabled={processing} className="mx-auto text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">Cancel</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
